// チャート関連の定数定義

// 色定義
export const CHART_COLORS = {
  GENERAL_INCOME: 'rgba(46, 125, 50, 0.8)',
  GENERAL_EXPENSE: 'rgba(211, 47, 47, 0.8)',
  RESERVE_INCOME: 'rgba(102, 187, 106, 0.7)',
  RESERVE_EXPENSE: 'rgba(239, 83, 80, 0.7)',
  BALANCE_LINE: 'rgba(31, 119, 180, 1)',
  CUMULATIVE_BALANCE: 'rgba(44, 62, 130, 1)',
  
  // 円グラフ用カテゴリ色（デフォルト）
  PIE_COLORS: [
    'rgba(46, 125, 50, 0.8)',   // 緑
    'rgba(25, 118, 210, 0.8)',  // 青
    'rgba(211, 47, 47, 0.8)',   // 赤
    'rgba(245, 124, 0, 0.8)',   // オレンジ
    'rgba(156, 39, 176, 0.8)',  // 紫
    'rgba(255, 193, 7, 0.8)',   // 黄色
    'rgba(96, 125, 139, 0.8)',  // グレー
    'rgba(76, 175, 80, 0.8)',   // ライトグリーン
    'rgba(63, 81, 181, 0.8)',   // インディゴ
    'rgba(255, 87, 34, 0.8)',   // ディープオレンジ
  ],
} as const;

// ホバー時の色（透明度を上げたバージョン）
export const CHART_HOVER_COLORS = {
  GENERAL_INCOME: 'rgba(46, 125, 50, 1)',
  GENERAL_EXPENSE: 'rgba(211, 47, 47, 1)',
  RESERVE_INCOME: 'rgba(102, 187, 106, 1)',
  RESERVE_EXPENSE: 'rgba(239, 83, 80, 1)',
  BALANCE_LINE: 'rgba(31, 119, 180, 1)',
  CUMULATIVE_BALANCE: 'rgba(44, 62, 130, 1)',
} as const;

// デフォルトチャートオプション
export const DEFAULT_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    tooltip: {
      mode: 'index' as const,
      intersect: true,
    },
  },
  scales: {
    x: {
      stacked: false,
    },
    y: {
      beginAtZero: true,
      stacked: false,
    },
  },
  interaction: {
    mode: 'nearest' as const,
    intersect: true,
  },
} as const;

// 複合グラフ（棒＋線）用オプション
export const FINANCIAL_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  scales: {
    x: {
      stacked: true,
      title: {
        display: true,
        text: '期間',
      },
    },
    y: {
      beginAtZero: true,
      stacked: true,
      position: 'left' as const,
      title: {
        display: true,
        text: '金額（円）',
      },
    },
  },
} as const;

// 円グラフ用オプション
export const PIE_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right' as const,
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || '';
          const value = context.parsed || 0;
          const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ¥${value.toLocaleString()} (${percentage}%)`;
        },
      },
    },
  },
} as const;

// チャートサイズ設定
export const CHART_DIMENSIONS = {
  DEFAULT_HEIGHT: 400,
  MOBILE_HEIGHT: 300,
  DESKTOP_HEIGHT: 500,
  PIE_HEIGHT: 350,
} as const;

// アニメーション設定
export const CHART_ANIMATIONS = {
  DURATION: 750,
  EASING: 'easeInOutQuart',
} as const;

// フォント設定
export const CHART_FONTS = {
  FAMILY: '"Roboto", "Helvetica", "Arial", sans-serif',
  SIZE: 12,
  TITLE_SIZE: 16,
  LEGEND_SIZE: 11,
} as const;

// 数値フォーマット用設定
export const NUMBER_FORMAT = {
  LOCALE: 'ja-JP',
  CURRENCY: 'JPY',
  MINIMUM_FRACTION_DIGITS: 0,
  MAXIMUM_FRACTION_DIGITS: 0,
} as const;

// ツールチップフォーマット関数
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat(NUMBER_FORMAT.LOCALE, {
    style: 'currency',
    currency: NUMBER_FORMAT.CURRENCY,
    minimumFractionDigits: NUMBER_FORMAT.MINIMUM_FRACTION_DIGITS,
    maximumFractionDigits: NUMBER_FORMAT.MAXIMUM_FRACTION_DIGITS,
  }).format(value);
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat(NUMBER_FORMAT.LOCALE, {
    minimumFractionDigits: NUMBER_FORMAT.MINIMUM_FRACTION_DIGITS,
    maximumFractionDigits: NUMBER_FORMAT.MAXIMUM_FRACTION_DIGITS,
  }).format(value);
};

// アカウントタイプ別ラベル
export const ACCOUNT_LABELS = {
  general: '一般会計',
  reserve: '積立会計',
  both: '全会計',
} as const;

// 取引タイプ別ラベル
export const TRANSACTION_LABELS = {
  income: '収入',
  expense: '支出',
} as const;